import React, { useCallback, useMemo, useState } from "react";
import { Create, useAutocomplete } from "@refinedev/mui";
import { Autocomplete, Box, TextField, Typography, Alert } from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { IResourceComponentsProps, useCustom } from "@refinedev/core";
import { NumericFormatCustom } from "components/inputs/custom-numeric-input";

// Types for better type safety
interface UserOption {
  sub: string;
  label: string;
}

interface FundOption {
  fundId: string;
  label: string;
}

interface User {
  sub: string;
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  zip: string;
  state: string;
  addressLine1: string;
  addressLine2?: string;
}

interface EntityInformation {
  entityName: string;
  city: string;
  zip: string;
  state: string;
  firstAddressLine: string;
  secondAddressLine?: string;
}

interface FundAccount {
  fund: {
    fundAccountId: string;
    fundAccountName: string;
  };
  entityInformation?: EntityInformation;
}

interface FormData {
  user: string;
  fund: string;
  estimatedAmount: number;
  description: string;
  reference: string;
  extraNotes?: string;
  name: string;
  donorEmail: string;
  emailToNotify?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  sub?: string;
  fundId?: string;
  type?: string;
  contributionInformation?: object;
  contributionReport?: object;
}

export const ContributionTransactionCreate: React.FC<
  IResourceComponentsProps
> = () => {
  // Form state
  const {
    refineCore: { formLoading, onFinish },
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const [selectedUserSub, setSelectedUserSub] = useState<string>("");
  const [selectedFund, setSelectedFund] = useState<string>("");

  const { autocompleteProps: users } = useAutocomplete({
    resource: "Users",
  });

  const {
    data: userFunds,
    isLoading: userFundsLoading,
    error: userFundsError,
  } = useCustom({
    url: `users/sub`,
    method: "get",
    config: {
      query: { sub: selectedUserSub },
    },
    queryOptions: {
      enabled: !!selectedUserSub,
    },
  });

  const userOptions = useMemo((): UserOption[] => {
    if (!users.options || users.loading) return [];

    return users.options.map((user: User, index: number) => ({
      sub: user.sub,
      label: `${index + 1} - ${user.firstName} ${user.lastName}`,
    }));
  }, [users.options, users.loading]);

  const userFundsOptions = useMemo((): FundOption[] => {
    if (!userFunds?.data?.fundAccounts) return [];

    return userFunds.data.fundAccounts.map(
      (fundAccount: FundAccount, index: number) => ({
        fundId: fundAccount.fund.fundAccountId,
        label: `${index + 1} - ${fundAccount.fund.fundAccountName}`,
      })
    );
  }, [userFunds?.data?.fundAccounts]);

  // Fixed bug: use === instead of = in find
  const fillContributionReport = useCallback(
    (fundId: string) => {
      const foundUser = users.options?.find(
        (item: User) => item.sub === selectedUserSub
      );
      if (!foundUser) return;

      const selectedUserFund = userFunds?.data?.fundAccounts?.find(
        (fund: FundAccount) => fund.fund.fundAccountId === fundId
      );

      if (!selectedUserFund) return;

      // Set email fields
      setValue("donorEmail", foundUser.email);
      setValue("emailToNotify", foundUser.email);

      // Use entity information if available, otherwise use user info
      if (selectedUserFund.entityInformation) {
        const { entityInformation } = selectedUserFund;
        setValue("name", entityInformation.entityName);
        setValue("city", entityInformation.city);
        setValue("zip", entityInformation.zip);
        setValue("state", entityInformation.state);
        setValue("addressLine1", entityInformation.firstAddressLine);
        setValue("addressLine2", entityInformation.secondAddressLine || "");
      } else {
        setValue("name", `${foundUser.firstName} ${foundUser.lastName}`);
        setValue("city", foundUser.city);
        setValue("zip", foundUser.zip);
        setValue("state", foundUser.state);
        setValue("addressLine1", foundUser.addressLine1);
        setValue("addressLine2", foundUser.addressLine2 || "");
      }
    },
    [selectedUserSub, setValue, userFunds?.data?.fundAccounts, users.options]
  );

  // Form submission handler
  const handleFormSubmit = useCallback(
    (e: React.BaseSyntheticEvent) => {
      const formData = getValues();

      // Set additional form values
      setValue("sub", selectedUserSub);
      setValue("fundId", selectedFund);
      setValue("type", "OTHER");

      setValue("contributionInformation", {
        estimatedAmount: formData.estimatedAmount,
        description: formData.description,
      });

      setValue("contributionReport", {
        name: formData.name,
        donorEmail: formData.donorEmail,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
      });

      handleSubmit(onFinish, () => false)(e);
    },
    [getValues, setValue, selectedUserSub, selectedFund, handleSubmit, onFinish]
  );

  // User selection handler
  const handleUserChange = useCallback((_: any, value: UserOption | null) => {
    const userSub = value?.sub || "";
    setSelectedUserSub(userSub);
    setSelectedFund(""); // Reset fund selection when user changes
  }, []);

  // Fund selection handler
  const handleFundChange = useCallback(
    (_: any, value: FundOption | null) => {
      const fundId = value?.fundId || "";
      setSelectedFund(fundId);
      if (fundId) {
        fillContributionReport(fundId);
      }
    },
    [fillContributionReport]
  );

  return (
    <Create
      resource="/contribution-transactions"
      title={
        <Typography variant="body1" fontWeight="bold">
          Create Contribution on behalf of the user
        </Typography>
      }
      isLoading={formLoading}
      saveButtonProps={{
        resource: "/contribution-transactions",
        onClick: handleFormSubmit,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        {/* User Selection */}
        <Typography variant="body1" color="primary" fontWeight="bold">
          Please select a user
        </Typography>

        <Autocomplete
          disablePortal
          loading={users.loading}
          options={userOptions}
          onChange={handleUserChange}
          isOptionEqualToValue={(option, value) => option.sub === value.sub}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              {option.label}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Please choose or type user name"
              {...register("user", {
                required: "Please choose a user",
              })}
              error={!!errors.user}
              helperText={errors.user?.message as string}
            />
          )}
        />

        {/* Fund Selection - Only show when user is selected */}
        {selectedUserSub && (
          <>
            {userFundsError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error loading user funds. Please try selecting a different user.
              </Alert>
            )}

            {!userFundsLoading && userFunds?.data?.fundAccounts && (
              <>
                <Typography
                  variant="body1"
                  color="primary"
                  fontWeight="bold"
                  sx={{ mt: 2 }}
                >
                  Please select one of the user's fund accounts
                </Typography>

                <Autocomplete
                  disablePortal
                  options={userFundsOptions}
                  onChange={handleFundChange}
                  isOptionEqualToValue={(option, value) =>
                    option.fundId === value.fundId
                  }
                  getOptionLabel={(option) => option.label}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      {option.label}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Please choose a fund account"
                      {...register("fund", {
                        required: "Please choose a fund",
                      })}
                      error={!!errors.fund}
                      helperText={errors.fund?.message as string}
                    />
                  )}
                />
              </>
            )}

            {userFundsLoading && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading user funds...
              </Typography>
            )}
          </>
        )}

        {/* Form Fields - Only show when fund is selected */}
        {selectedFund && (
          <>
            {/* Contribution Information */}
            <Typography
              variant="body1"
              color="primary"
              fontWeight="bold"
              sx={{ pt: 3 }}
            >
              Contribution Information
            </Typography>

            <TextField
              {...register("estimatedAmount", {
                required: "Estimated amount is required",
                min: { value: 0.01, message: "Amount must be greater than 0" },
              })}
              error={!!errors.estimatedAmount}
              helperText={errors.estimatedAmount?.message as string}
              margin="normal"
              fullWidth
              label="Estimated Amount"
              InputProps={{
                inputComponent: NumericFormatCustom as any,
              }}
            />

            <TextField
              {...register("description", {
                required: "Description is required",
                minLength: {
                  value: 5,
                  message: "Description must be at least 5 characters",
                },
              })}
              error={!!errors.description}
              helperText={errors.description?.message as string}
              margin="normal"
              fullWidth
              label="Description"
              multiline
              rows={2}
            />

            <TextField
              {...register("reference", {
                required: "Reference is required",
              })}
              error={!!errors.reference}
              helperText={errors.reference?.message as string}
              margin="normal"
              fullWidth
              label="Reference"
            />

            <TextField
              {...register("extraNotes")}
              margin="normal"
              fullWidth
              label="Extra Notes (Optional)"
              multiline
              rows={3}
            />

            {/* Contribution Report */}
            <Typography
              variant="body1"
              color="primary"
              fontWeight="bold"
              sx={{ pt: 3, pb: 2 }}
            >
              Contribution Report
            </Typography>

            <TextField
              {...register("name", { required: "Name is required" })}
              error={!!errors.name}
              helperText={errors.name?.message as string}
              margin="normal"
              fullWidth
              label="Name"
            />

            <TextField
              {...register("donorEmail", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              error={!!errors.donorEmail}
              helperText={errors.donorEmail?.message as string}
              margin="normal"
              fullWidth
              label="Email"
              type="email"
            />

            <TextField
              {...register("addressLine1", { required: "Address is required" })}
              error={!!errors.addressLine1}
              helperText={errors.addressLine1?.message as string}
              margin="normal"
              fullWidth
              label="Address Line 1"
            />

            <TextField
              {...register("addressLine2")}
              margin="normal"
              fullWidth
              label="Address Line 2 (Optional)"
            />

            <TextField
              {...register("city", { required: "City is required" })}
              error={!!errors.city}
              helperText={errors.city?.message as string}
              margin="normal"
              fullWidth
              label="City"
            />

            <TextField
              {...register("state", { required: "State is required" })}
              error={!!errors.state}
              helperText={errors.state?.message as string}
              margin="normal"
              fullWidth
              label="State"
            />

            <TextField
              {...register("zip", {
                required: "ZIP code is required",
                pattern: {
                  value: /^\d{5}(-\d{4})?$/,
                  message: "Invalid ZIP code format",
                },
              })}
              error={!!errors.zip}
              helperText={errors.zip?.message as string}
              margin="normal"
              fullWidth
              label="ZIP Code"
            />
          </>
        )}
      </Box>
    </Create>
  );
};
